import { Loader } from "@/components/loader/Loader.tsx";
import { summarizeMeetingPreference } from "@/shared/meetingPreferenceSummary.ts";
import {
  useCreateMeetingPreference,
  useDeleteMeetingPreference,
  useMeetingPreferences,
  useUpdateMeetingPreference,
} from "@/components/preferences/useMeetingPreferences.tsx";
import {
  summarizeAvailabilityDays,
  WeeklyTimePicker,
} from "@/components/preferences/WeeklyTimePicker.tsx";
import { Button } from "@/components/ui/button.tsx";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.tsx";
import { useIsMobile } from "@/hooks/use-mobile.tsx";
import { Input } from "@/components/ui/input.tsx";
import { Label } from "@/components/ui/label.tsx";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select.tsx";
import { Textarea } from "@/components/ui/textarea.tsx";
import { Toggle } from "@/components/ui/toggle.tsx";
import { cn } from "@/lib/utils.ts";
import type {
  MeetingPreferenceCreateReq,
  MeetingPreferenceResp,
  PreferredDaysResp,
} from "@/types/meetingPreferences.ts";
import { ChevronDown, CirclePlus, X } from "lucide-react";
import { useState } from "react";

const DURATION_OPTIONS = [
  { value: 15, label: "15 minutes" },
  { value: 30, label: "30 minutes" },
  { value: 45, label: "45 minutes" },
  { value: 60, label: "1 hour" },
  { value: 90, label: "1 hour 30 minutes" },
  { value: 120, label: "2 hours" },
];

export const MeetingPreferencesList = () => {
  const { data, isLoading } = useMeetingPreferences();
  const [editing, setEditing] = useState<MeetingPreferenceResp | undefined>(undefined);
  const [creating, setCreating] = useState(false);

  if (isLoading) {
    return (
      <div className="py-6 flex justify-center">
        <Loader size="sm" />
      </div>
    );
  }

  const prefs = data ?? [];

  return (
    <div className="space-y-3">
      {prefs.length > 0 ? (
        <ul className="divide-y rounded-lg bg-white overflow-hidden">
          {prefs.map((pref) => (
            <li key={pref.id}>
              <Button
                variant="ghost"
                onClick={() => setEditing(pref)}
                className="w-full h-auto px-4 py-3 flex items-center justify-between gap-3 font-normal rounded-none"
                data-statsig-name="meeting-prefs-row-edit"
              >
                <div className="min-w-0 text-left">
                  <div className="text-sm font-medium truncate">{pref.meetingType}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {summarizePref(pref)}
                  </div>
                </div>
                <span className="text-xs text-primary">Edit</span>
              </Button>
            </li>
          ))}
        </ul>
      ) : (
        <div className="text-sm text-muted-foreground italic">
          No meeting preferences yet. Add one to teach the scheduler how to handle a meeting type.
        </div>
      )}

      <Button
        variant="outline"
        size="sm"
        onClick={() => setCreating(true)}
        data-statsig-name="meeting-prefs-add-open"
      >
        <CirclePlus className="size-4 mr-1.5" />
        Add meeting type
      </Button>

      {creating && (
        <MeetingPreferenceDialog
          mode="create"
          open
          onOpenChange={(open) => !open && setCreating(false)}
        />
      )}
      {editing && (
        <MeetingPreferenceDialog
          mode="edit"
          pref={editing}
          open
          onOpenChange={(open) => !open && setEditing(undefined)}
        />
      )}
    </div>
  );
};

// Row summary lives in shared/client so native's list reads identically.
const summarizePref = summarizeMeetingPreference;

type DialogProps =
  | { mode: "create"; pref?: never; open: boolean; onOpenChange: (open: boolean) => void }
  | {
      mode: "edit";
      pref: MeetingPreferenceResp;
      open: boolean;
      onOpenChange: (open: boolean) => void;
    };

const MeetingPreferenceDialog = (props: DialogProps) => {
  const isMobile = useIsMobile();
  const { open, onOpenChange } = props;
  const title = props.mode === "create" ? "Add Meeting Type" : "Edit Meeting Type";

  if (isMobile) {
    if (!open) return null;
    return (
      <div className="fixed inset-0 z-50 bg-background flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b shrink-0">
          <h2 className="text-base font-semibold">{title}</h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onOpenChange(false)}
            className="rounded-full"
            data-statsig-name="meeting-prefs-dialog-close-mobile"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4">
          <MeetingPreferenceForm
            pref={props.mode === "edit" ? props.pref : undefined}
            onClose={() => onOpenChange(false)}
            isMobile
          />
        </div>
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[600px] max-h-[calc(100vh-2.5rem)] grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden"
        onEscapeKeyDown={(e) => e.stopPropagation()}
        // Don't let a stray click outside discard unsaved edits — the user must
        // explicitly Cancel (or Save) to leave the dialog.
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <MeetingPreferenceForm
          pref={props.mode === "edit" ? props.pref : undefined}
          onClose={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  );
};

const MeetingPreferenceForm = ({
  pref,
  onClose,
  isMobile,
}: {
  pref?: MeetingPreferenceResp;
  onClose: () => void;
  isMobile?: boolean;
}) => {
  const [meetingType, setMeetingType] = useState(pref?.meetingType ?? "");
  const [description, setDescription] = useState(pref?.description ?? "");
  const initDuration = pref?.typicalDuration;
  const initIsCustom =
    initDuration != null && !DURATION_OPTIONS.some((o) => o.value === initDuration);
  const [typicalDuration, setTypicalDuration] = useState<number | undefined>(initDuration);
  const [showCustom, setShowCustom] = useState(initIsCustom);
  const [topicFormat, setTopicFormat] = useState(pref?.topicFormat ?? "");
  const [isVirtual, setIsVirtual] = useState(pref?.isVirtual ?? true);
  const [roughTimeRange, setRoughTimeRange] = useState(pref?.roughTimeRange ?? "");
  const [availability, setAvailability] = useState<PreferredDaysResp>(pref?.availability ?? {});
  const [showAvailability, setShowAvailability] = useState(false);

  const createMutation = useCreateMeetingPreference();
  const updateMutation = useUpdateMeetingPreference();
  const deleteMutation = useDeleteMeetingPreference();
  const isPending = createMutation.isPending || updateMutation.isPending;
  const isDeleting = deleteMutation.isPending;

  const handleSave = () => {
    const payload: MeetingPreferenceCreateReq = {
      meetingType,
      description: description || undefined,
      typicalDuration,
      topicFormat: topicFormat || undefined,
      isVirtual,
      roughTimeRange: roughTimeRange || undefined,
      availability,
    };
    if (pref) {
      updateMutation.mutate({ id: pref.id, data: payload }, { onSuccess: () => onClose() });
    } else {
      createMutation.mutate(payload, { onSuccess: () => onClose() });
    }
  };

  const handleDelete = () => {
    if (!pref) return;
    deleteMutation.mutate(pref.id, { onSuccess: () => onClose() });
  };

  return (
    <>
      <div className={cn("space-y-4", !isMobile && "overflow-y-auto -mx-6 px-6")}>
        <div className="space-y-2">
          <Label htmlFor="meetingType">Meeting Type</Label>
          <Input
            id="meetingType"
            value={meetingType}
            onChange={(e) => setMeetingType(e.target.value)}
            placeholder="e.g. 1:1, Team Sync, Interview"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            placeholder="How to recognize this meeting type, e.g. recurring 1:1 with direct reports"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="typicalDuration">Typical Duration</Label>
          <Select
            value={showCustom ? "custom" : String(typicalDuration ?? "")}
            onValueChange={(v) => {
              if (v === "custom") {
                setShowCustom(true);
                setTypicalDuration(undefined);
              } else {
                setShowCustom(false);
                setTypicalDuration(Number(v));
              }
            }}
          >
            <SelectTrigger id="typicalDuration">
              <SelectValue placeholder="Select duration..." />
            </SelectTrigger>
            <SelectContent>
              {DURATION_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={String(opt.value)}>
                  {opt.label}
                </SelectItem>
              ))}
              <SelectItem value="custom">Custom</SelectItem>
            </SelectContent>
          </Select>
          {showCustom && (
            <Input
              type="number"
              min={1}
              value={typicalDuration ?? ""}
              onChange={(e) => {
                const val = e.target.value;
                if (val) setTypicalDuration(Number(val));
                else setTypicalDuration(undefined);
              }}
              placeholder="Minutes"
            />
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="topicFormat">Naming Convention</Label>
          <Input
            id="topicFormat"
            value={topicFormat}
            onChange={(e) => setTopicFormat(e.target.value)}
            placeholder="e.g. [Name] / [Name] 1:1"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="roughTimeRange">Rough Time Range</Label>
          <Input
            id="roughTimeRange"
            value={roughTimeRange}
            onChange={(e) => setRoughTimeRange(e.target.value)}
            placeholder="e.g. in the next 7 days, within 2 weeks"
          />
        </div>
        <div className="flex items-center gap-2">
          <Toggle
            checked={isVirtual}
            onCheckedChange={setIsVirtual}
            leftLabel="In-person"
            rightLabel="Virtual"
          />
        </div>
        {isMobile ? (
          <>
            <div className="space-y-2">
              <Button
                variant="ghost"
                onClick={() => setShowAvailability(true)}
                className="flex items-center justify-between w-full h-auto p-0 font-normal hover:bg-transparent"
                data-statsig-name="meeting-prefs-availability-toggle-mobile"
              >
                <Label className="cursor-pointer">Availability</Label>
                <span className="flex items-center gap-1 text-sm font-medium text-primary">
                  {summarizeAvailabilityDays(availability) ?? "Set availability"}
                  <ChevronDown className="w-4 h-4" />
                </span>
              </Button>
            </div>
            {showAvailability && (
              <WeeklyTimePicker
                value={availability}
                onChange={setAvailability}
                onClose={() => setShowAvailability(false)}
              />
            )}
          </>
        ) : (
          <div className="space-y-2">
            <Label>Availability</Label>
            <WeeklyTimePicker value={availability} onChange={setAvailability} />
          </div>
        )}
      </div>
      <DialogFooter
        className={cn(isMobile && "sticky bottom-0 bg-background border-t py-3 -mx-4 px-4")}
      >
        {pref && (
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={isPending || isDeleting}
            className="mr-auto"
            data-statsig-name="meeting-prefs-delete"
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        )}
        <Button
          variant="secondary"
          onClick={onClose}
          disabled={isPending || isDeleting}
          data-statsig-name="meeting-prefs-cancel"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={isPending || isDeleting || !meetingType.trim()}
          data-statsig-name="meeting-prefs-save"
        >
          {isPending ? "Saving..." : "Save"}
        </Button>
      </DialogFooter>
    </>
  );
};
